import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Col,
  Input,
  Label,
  Row,
  Spinner,
  FormGroup,
} from 'reactstrap';
import { Btn } from '../../../AbstractElements';
import { get, postJson, putJson, del } from '../../../hook/request';
import { toast } from 'react-toastify';

// -------- Types --------

type ContactRow = {
  ID: number;
  typeID: number; 
  notifica: boolean;
  email: string;
};

type CategoryApi = {
  ID: number;
  meta?: unknown; 
  codice?: string;
  ordine?: number;
  abilitato?: 0 | 1;
};

type CategoryOption = { id: number; label: string };

// -------- Helpers --------

function parseMetaName(meta: unknown, lang = 'it'): string {
  if (!meta) return '';
  try {
    const obj = typeof meta === 'string' ? JSON.parse(meta) : (meta as any);
    const byLang = obj?.[lang] || {};
    return (byLang.nome ?? '').toString();
  } catch {
    return '';
  }
}

function toBoolNotifica(v: any): boolean {
  // supporta 0/1, "0"/"1", true/false
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v === 1;
  if (typeof v === 'string') return v === '1' || v.toLowerCase() === 'true';
  return false;
}

// -------- Component --------

export default function ContactAccordion() {
  const [open, setOpen] = useState<'contatti' | ''>('contatti');
  const toggle = (id: 'contatti') => setOpen(prev => (prev === id ? '' : id));

  const [loading, setLoading] = useState(true);
  const [savingRow, setSavingRow] = useState<number | null>(null);
  const [deletingRow, setDeletingRow] = useState<number | null>(null);

  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [rows, setRows] = useState<ContactRow[]>([]);

  const [newCategory, setNewCategory] = useState<string>('');
  const [newEmail, setNewEmail] = useState<string>('');
  const [newNotifica, setNewNotifica] = useState<boolean>(false);

  const categoryMap = useMemo(
    () => new Map(categories.map(c => [c.id, c.label])),
    [categories]
  );

  // Load categories + contacts
  useEffect(() => {
    (async () => {
      try {
        // categories
        const catRes = await get<CategoryApi[] | { data?: CategoryApi[] }>(
          `/categories/parent?ID=661`
        );
        const catRoot: any = (catRes as any)?.data ?? catRes;
        const catArr: CategoryApi[] = Array.isArray(catRoot)
          ? catRoot
          : Array.isArray(catRoot?.data)
          ? catRoot.data
          : [];
        const catOpts: CategoryOption[] = catArr
          .filter(c => (c.abilitato ?? 1) === 1)
          .sort((a, b) => (a.ordine ?? 0) - (b.ordine ?? 0))
          .map(c => ({
            id: Number(c.ID),
            label: parseMetaName(c.meta, 'it') || c.codice || String(c.ID),
          }));
        setCategories(catOpts);

        // contacts
        const res = await get<ContactRow[] | { data?: ContactRow[] }>(
          `/azienda/contatti`
        );
        const root: any = (res as any)?.data ?? res;
        const arr: any[] = Array.isArray(root)
          ? root
          : Array.isArray(root?.data)
          ? root.data
          : [];
        setRows(
          arr.map(r => ({
            ID: Number(r.ID),
            typeID: Number(r.typeID),
            email: r.email,
            notifica: toBoolNotifica(r.notifica),
          }))
        );
      } catch (e: any) {
        toast.error(e?.message || 'Errore nel caricamento contatti');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const resetNew = () => {
    setNewCategory('');
    setNewEmail('');
    setNewNotifica(false);
  };

  const reloadRows = async () => {
    const list = await get<ContactRow[] | { data?: ContactRow[] }>(
      `/azienda/contatti`
    );
    const root: any = (list as any)?.data ?? list;
    const arr: any[] = Array.isArray(root)
      ? root
      : Array.isArray(root?.data)
      ? root.data
      : [];
    setRows(
      arr.map(r => ({
        ID: Number(r.ID),
        typeID: Number(r.typeID),
        email: r.email,
        notifica: toBoolNotifica(r.notifica),
      }))
    );
  };

  const handleAdd = async () => {
    if (!newCategory || !newEmail) {
      toast.warn("Seleziona una categoria e inserisci un'email");
      return;
    }
    const payload = {
      categoryID: Number(newCategory),
      email: newEmail.trim(),
      notifica: newNotifica ? 1 : 0, // <-- invio 1/0
      user_email: localStorage.getItem('email') || 'supporto@i-factory.biz',
    };
    try {
      setSavingRow(-1);
      await postJson(`/azienda/contatti`, payload);
      toast.success('Contatto aggiunto');
      await reloadRows();
      resetNew();
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message ?? e?.message ?? 'Errore aggiunta'
      );
    } finally {
      setSavingRow(null);
    }
  };

  const handleUpdate = async (row: ContactRow) => {
    const payload = {
      id: row.ID,
      categoryID: row.typeID,
      email: row.email.trim(),
      notifica: row.notifica ? 1 : 0, 
      user_email: localStorage.getItem('email') || 'supporto@i-factory.biz',
    };
    try {
      setSavingRow(row.ID);
      await putJson(`/azienda/contatti`, payload);
      toast.success('Contatto aggiornato');
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message ?? e?.message ?? 'Errore aggiornamento'
      );
    } finally {
      setSavingRow(null);
    }
  };

  const handleDelete = async (row: ContactRow) => {
    if (!window.confirm('Eliminare questo contatto?')) return;

    try {
      setDeletingRow(row.ID);
      await del(`/azienda/contatti?ID=${encodeURIComponent(String(row.ID))}`);
      setRows(prev => prev.filter(r => r.ID !== row.ID));
      toast.success('Contatto eliminato');
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message ?? e?.message ?? 'Errore eliminazione'
      );
    } finally {
      setDeletingRow(null);
    }
  };


  if (loading) {
    return (
      <CardLike>
        <div className="d-flex justify-content-center py-5">
          <Spinner />
        </div>
      </CardLike>
    );
  }

  return (
    <Accordion
      open={open}
      toggle={() => toggle('contatti')}
      className="icons-accordion"
    >
      <AccordionItem>
        <AccordionHeader
          targetId={'contatti'}
          className="gap-2 text-secondary bg-light-secondary"
        >
          <span className="font-secondary">Contatti</span>
          {open === 'contatti' ? (
            <i className="iconly-Arrow-Up-2 icli ms-auto icon font-secondary" />
          ) : (
            <i className="iconly-Arrow-Down-2 icli ms-auto icon font-secondary" />
          )}
        </AccordionHeader>
        <AccordionBody accordionId={'contatti'}>
          {/* Riga di inserimento */}
          <Row className="g-3 align-items-end mb-3">
            <Col md={4}>
              <Label>Categoria</Label>
              <Input
                type="select"
                value={newCategory}
                onChange={e => setNewCategory(e.target.value)}
              >
                <option value="">Seleziona…</option>
                {categories.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={5}>
              <Label>Email</Label>
              <Input
                type="email"
                placeholder="es. nome@azienda.it"
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
              />
            </Col>
            <Col md={1}>
              <FormGroup className="checkbox-radio" check inline>
                <Input
                  id="inline-new-notifica"
                  type="checkbox"
                  name="notifica"
                  checked={newNotifica}
                  onChange={e => setNewNotifica(e.target.checked)}
                />
                <Label htmlFor="inline-new-notifica" check>
                  Notifica
                </Label>
              </FormGroup>
            </Col>
            <Col md={2} className="d-grid">
              <Btn
                color="success"
                className="text-white"
                onClick={handleAdd}
                disabled={savingRow === -1}
              >
                Aggiungi
              </Btn>
            </Col>
          </Row>

          {/* Header lista */}
          <Row className="g-3 mt-2 mb-1">
            <Col md={4}>
              <strong>Categoria</strong>
            </Col>
            <Col md={5}>
              <strong>Email</strong>
            </Col>
            <Col md={1} className='d-flex justify-content-center'>
              <strong>Notifica</strong>
            </Col>
            <Col md={2} className="text-end">
              <strong>Azioni</strong>
            </Col>
          </Row>
          <hr className="mt-0" />

          {rows.map(row => (
            <Row key={row.ID} className="g-3 align-items-center mb-3">
              <Col md={4}>
                <Input
                  type="select"
                  value={row.typeID}
                  onChange={e =>
                    setRows(prev =>
                      prev.map(r =>
                        r.ID === row.ID
                          ? { ...r, typeID: Number(e.target.value) }
                          : r
                      )
                    )
                  }
                >
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </Input>
              </Col>
              <Col md={5}>
                <Input
                  type="email"
                  value={row.email}
                  onChange={e =>
                    setRows(prev =>
                      prev.map(r =>
                        r.ID === row.ID
                          ? { ...r, email: e.target.value }
                          : r
                      )
                    )
                  }
                />
              </Col>
              <Col md={1}>
                <FormGroup className="checkbox-radio d-flex justify-content-center" check inline>
                  <Input
                    type="checkbox"
                    id={`row-notifica-${row.ID}`}
                    checked={row.notifica}
                    onChange={e =>
                      setRows(prev =>
                        prev.map(r =>
                          r.ID === row.ID
                            ? { ...r, notifica: e.target.checked }
                            : r
                        )
                      )
                    }
                  />
                  <Label htmlFor={`row-notifica-${row.ID}`} check>
                    {' '}
                  </Label>
                </FormGroup>
              </Col>
              <Col md={2} className="d-flex justify-content-end gap-2">
                <Btn
                  color="info"
                  className="text-white"
                  onClick={() => handleUpdate(row)}
                  disabled={savingRow === row.ID}
                >
                  Aggiorna
                </Btn>
                <Btn
                  color="danger"
                  className="text-white"
                  onClick={() => handleDelete(row)}
                  disabled={deletingRow === row.ID}
                >
                  Elimina
                </Btn>
              </Col>
            </Row>
          ))}
        </AccordionBody>
      </AccordionItem>
    </Accordion>
  );
}

function CardLike({ children }: { children: React.ReactNode }) {
  return (
    <div className="card">
      <div className="card-body">{children}</div>
    </div>
  );
}
